'use client'

import { memo } from 'react'
import { IconInfoCircle } from '@tabler/icons-react'

import { Cart } from 'modules/cart'
import Button from 'shared/ui/Button'
import { theme } from 'shared/ui/utils/designTokens'

import { EditSaleOrderStatus , getProperReturnLabel, getProperReturnValue } from '../../utils'

import SubmitEditSaleOrderModalDetailsSection from './SubmitEditSaleOrderModalDetailsSection'
import SubmitEditSaleOrderModalSummary from './SubmitEditSaleOrderModalSummary'

import styles from './SubmitEditSaleOrderModalContent.module.scss'

interface ModalSubmitEditContentProps {
  editStatusObject: EditSaleOrderStatus
  paymentLoading: boolean
  onBlikModalOpen: () => void
  onCreateOrder: () => void
  onClose: () => void
  cart: Cart | null
}

export const ModalSubmitEditContent = memo(
  ({
    editStatusObject,
    paymentLoading,
    onBlikModalOpen,
    onCreateOrder,
    onClose,
    cart
  }: ModalSubmitEditContentProps) => {
    if (editStatusObject === null) return
    const minOrderValueMissing = cart?.minOrderValueMissing?.value || null
    const isMinOrderValueMissing =
      minOrderValueMissing !== null && minOrderValueMissing > 0
    const minOrderValueMissingFormatted =
      cart?.minOrderValueMissing?.formatted || null

    const getButtonLabel = () => {
      if (editStatusObject.wasOnDelivery) {
        return `Do zapłaty ${editStatusObject.payment.current.paid || '---'}`
      }

      return `${getProperReturnLabel(
        editStatusObject.payment.current.diffValue
      )} ${
        getProperReturnValue(editStatusObject.payment.current.diffValue) ||
        '---'
      }`
    }

    return (
      <div className={styles.modalContent}>
        <h2 className={styles.orderNumberText}>
          Nr zamówienia: <b>#{editStatusObject.orderNo}</b>
        </h2>

        <div className={styles.detailsList}>
          <SubmitEditSaleOrderModalDetailsSection editStatusObject={editStatusObject} />
          <SubmitEditSaleOrderModalSummary cart={cart} />
        </div>

        {!editStatusObject.wasOnDelivery && <PaymentInfo />}
        {minOrderValueMissing && minOrderValueMissing > 0 ? (
          <div className={styles.minOrderValueMissing}>
            Do minimalnej wartości koszyka brakuje:
            <span
              id='min_order_value_missing'
              dangerouslySetInnerHTML={{
                __html: minOrderValueMissingFormatted || ''
              }}
            />
          </div>
        ) : null}

        <div className={styles.buttonContainer}>
          <Button
            variant='primary'
            fullWidth
            onClick={
              editStatusObject.isBlik ? () => onBlikModalOpen() : onCreateOrder
            }
            disabled={paymentLoading || isMinOrderValueMissing}
            loading={paymentLoading}
          >
            {getButtonLabel()}
          </Button>
          <Button variant='neutralOutline' fullWidth onClick={onClose}>
            Anuluj
          </Button>
        </div>
      </div>
    )
  }
)

ModalSubmitEditContent.displayName = 'ModalSubmitEditContent'

const PaymentInfo = memo(() => (
  <div className={styles.additionalInfo}>
    <IconInfoCircle size={16} color={theme.colors.brand.secondary} />
    <p className={styles.additionalInfoText}>
      Po kliknięciu w przycisk potwierdzenia, zostaniesz przekierowany do bramki
      z płatnościami.
    </p>
  </div>
))

PaymentInfo.displayName = 'PaymentInfo'
